import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageCircle, ExternalLink, Users } from 'lucide-react';

export function ContactPage() {
  return (
    <>
      <Helmet>
        <title>Contact Us - Get in Touch | Promptliteracy.ai</title>
        <meta name="description" content="Contact Promptliteracy.ai for questions, feedback, collaboration opportunities, or educator support. We're here to help with AI literacy education initiatives and partnerships." />
        
        {/* Open Graph */}
        <meta property="og:title" content="Contact Us - Get in Touch | Promptliteracy.ai" />
        <meta property="og:description" content="Contact Promptliteracy.ai for questions, feedback, collaboration opportunities, or educator support. We're here to help with AI literacy education initiatives." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://promptliteracy.ai/contact" />
        <meta property="og:site_name" content="Promptliteracy.ai" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Us - Get in Touch | Promptliteracy.ai" />
        <meta name="twitter:description" content="Contact Promptliteracy.ai for questions, feedback, collaboration opportunities, or educator support." />
        
        {/* Additional SEO */}
        <meta name="keywords" content="contact promptliteracy, AI education support, educator collaboration, feedback, partnership opportunities, AI literacy help" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://promptliteracy.ai/contact" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Promptliteracy.ai",
            "description": "Contact page for Promptliteracy.ai - AI literacy education platform",
            "url": "https://promptliteracy.ai/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "Promptliteracy.ai",
              "url": "https://promptliteracy.ai",
              "sameAs": [
                "https://twitter.com/@logikbug"
              ]
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-xl opacity-90 leading-relaxed">
              Connect with us on social media or through our Logikbug network for questions, feedback, or collaboration ideas.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-primary/3">
        <div className="container">
        <div className="max-w-4xl mx-auto">
          {/* Contact Methods */}
          <div className="grid md:grid-cols-2 gap-6 mb-12 max-w-2xl mx-auto">

            <Card className="text-center card-slate hover-lift border-secondary/20 hover:border-secondary/40 transition-all card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4 border border-secondary/20">
                  <MessageCircle className="h-6 w-6 text-secondary" />
                </div>
                <CardTitle className="text-lg text-primary">Social Media</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  Follow us for updates and join the conversation
                </p>
                <Button variant="outline" asChild className="border-slate-300 btn-blue-hover transition-colors">
                  <a href="https://twitter.com/@logikbug" target="_blank" rel="noopener noreferrer">
                    @logikbug
                    <ExternalLink className="ml-2 h-3 w-3" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center card-slate hover-lift border-primary/30 hover:border-primary/50 transition-all card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4 border border-primary/30">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg text-primary">Collaborate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  Interested in partnering with us?
                </p>
                <Button variant="outline" asChild className="border-slate-300 btn-blue-hover transition-colors">
                  <a href="https://logikbug.com" target="_blank" rel="noopener noreferrer">
                    Visit Logikbug
                    <ExternalLink className="ml-2 h-3 w-3" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <Card className="mb-12 card-accent">
            <CardHeader>
              <CardTitle className="text-2xl">Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Can I contribute content?</h3>
                  <p className="text-muted-foreground">
                    We welcome contributions from educators, researchers, and practitioners. Please reach out to 
                    discuss your ideas and our content standards.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">Is this content free to use?</h3>
                  <p className="text-muted-foreground">
                    Yes! Our educational content is designed to be freely accessible to support AI literacy education 
                    for learners of all ages.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">How do you ensure content quality?</h3>
                  <p className="text-muted-foreground">
                    All content goes through our editorial review process, focusing on research-backed methodologies, 
                    age-appropriate language, and ethical AI practices.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Can I use this in my classroom?</h3>
                  <p className="text-muted-foreground">
                    Absolutely! Our content is specifically designed for educational use in safe, structured learning 
                    environments including classrooms and homeschool settings.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Response Time Notice */}
          <Card className="border-primary/20 bg-primary/5 card-accent">
            <CardContent className="p-6 text-center">
              <h3 className="text-lg font-semibold mb-2">Response Time</h3>
              <p className="text-muted-foreground">
                We typically respond to emails within 2-3 business days. For urgent inquiries, 
                please mention "URGENT" in your subject line.
              </p>
            </CardContent>
          </Card>
        </div>
        </div>
      </section>
    </>
  );
}